import PingPongManager from './PingPongManager';

export interface WebSocketManagerOptions {
  startupTimeoutMs?: number;
  pingIntervalMs?: number;
  pongTimeoutMs?: number;
  enableHealthCheckLogging?: boolean
  onmessage?: (event: MessageEvent) => void;
  onopen?: () => void;
  onerror?: (error: Event) => void;
  onclose?: (event: Event) => void;
}

export class WebSocketManager {
  private url: string;

  // Specific to the connection
  private ws: WebSocket | null;

  // Connection management
  private connectionAttempts: number;
  private startupTimeoutMs: number;
  private pingIntervalMs: number;
  private pongTimeoutMs: number;
  private shouldReconnect: boolean = true;

  // Logging
  private enableHealthCheckLogging: boolean;

  // Handlers
  private onmessage: (event: MessageEvent) => void;
  private onopen: () => void;
  private onerror: (error: Event) => void;
  private onclose: (event: Event) => void;

  constructor(url: string, options: WebSocketManagerOptions = {}) {
    this.url = url;
    this.ws = null;
    this.connectionAttempts = 0;
    this.startupTimeoutMs = options.startupTimeoutMs || 5 * 1000;
    this.pingIntervalMs = options.pingIntervalMs || 1 * 1000;
    this.pongTimeoutMs = options.pongTimeoutMs || 5* 1000;
    this.enableHealthCheckLogging = options.enableHealthCheckLogging || false
    this.onmessage = options.onmessage || (() => {});
    this.onopen = options.onopen || (() => {});
    this.onerror = options.onerror || (() => {});
    this.onclose = options.onclose || (() => {});

    this.connect();
  }

  private connect(): void {
    this.connectionAttempts++
    console.log(`Connecting, attempt #${this.connectionAttempts}`)
    const ws = new WebSocket(this.url);
    this.ws = ws;
    let hasCalledReconnect = false;

    let pingPongManager: PingPongManager | undefined;
    let readyTimer: NodeJS.Timeout | undefined;

    // Failure modes:
    // Never opened
    // Closed
    // Ping pong
    let handleClose = () => {
        console.log('Closing and cleaning up WebSocket connection.')
        ws.onclose = () => {};
        ws.onerror = () => {};
        ws.onmessage = () => {};
        ws.onopen = () => {};
        handleClose = () => {}
        clearTimeout(readyTimer);
        readyTimer = undefined;
        pingPongManager?.stopPingsAndCleanup()
        this.ws = null;
        try {
            if (!hasCalledReconnect && this.shouldReconnect) {
                this.reconnectWithDelay();
                hasCalledReconnect = true;
            }
        } finally {
          try {
            ws.close();
          } catch {}
        }
        console.log('WebSocket connection closed and cleaned up.')
    }

    // Check to make sure it opens
    readyTimer = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
            console.log('WebSocket did not open within the timeout period');
            handleClose();
        }
    }, this.startupTimeoutMs);

    pingPongManager = new PingPongManager({
      websocket: ws,
      pingIntervalMs: this.pingIntervalMs,
      pongTimeoutMs: this.pongTimeoutMs,
      failureCallback: () => {
        console.log('Ping response not received within timeout, closing connection');
        handleClose();
      },
      enableLogging: this.enableHealthCheckLogging
    })

    this.ws.onopen = () => {
      console.log('Connected')
        this.connectionAttempts = 0;
        pingPongManager.startPings()
        this.onopen();
    };

    this.ws.onmessage = (event: MessageEvent) => {
        if (pingPongManager.checkPong(event.data)) return;
        this.onmessage(event);
    };

    this.ws.onerror = (error: Event) => {
        this.onerror(error);
    };

    this.ws.onclose = (event) => {
        this.onclose(event);
        console.log('WebSocket onclose event triggered');
        handleClose();
    };
  }

  private getRetryDelayMs(): number {
    const earlyDelays = [0, 250, 500, 2000];
    const phaseDelaysSeconds =    [5, 30, 2 * 60, 30 * 60];
    const phaseDurationsMinutes = [5, 15,     60, 24 * 60];
    const phaseAttempts = 
      phaseDurationsMinutes.map((d, i) => (d * 60) / phaseDelaysSeconds[i]);
    let i = this.connectionAttempts;

    if (i < earlyDelays.length) {
        return earlyDelays[i];
    }
    i -= earlyDelays.length;

    for (let j = 0; j < phaseDelaysSeconds.length; j++) {
        if (i < phaseAttempts[j]) {
            return phaseDelaysSeconds[j] * 1000;
        }
        i -= phaseAttempts[j];
    }
    return 24 * 60 * 60 * 1000; // Once per day indefinitely
  }

  private reconnectWithDelay(): void {
    const delay = this.getRetryDelayMs()
    console.log(`Reconnecting with delay: numAttempts=${this.connectionAttempts}, delay=${delay}ms`)
    setTimeout(() => this.connect(), this.getRetryDelayMs());
  }

  public isOpen(): boolean {
      return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  public send(data: string): void {
      if (this.ws !== null && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(data);
      } else {
          throw new Error('WebSocket not connected');
      }
  }

  public close(): void {
    this.shouldReconnect = false;
    this.ws?.close();
  }
}